import React, { useState, useEffect } from 'react';
import { Info } from 'lucide-react';
import { fetchYieldSheetsByVendorHistory } from '../services/api';

function isoToday() {
  return new Date().toISOString().split('T')[0];
}

function isoDaysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
}

function toApiDate(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${m}/${d}/${y}`;
}

function fmtNum(n, decimals = 1) {
  const v = Number(n);
  if (isNaN(v)) return '—';
  return v.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

function fmtMoney(n) {
  const v = Number(n);
  if (isNaN(v)) return '$0.00';
  return `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtPct(n) {
  const v = Number(n);
  if (isNaN(v)) return '—';
  return `${v.toFixed(1)}%`;
}

export default function VendorSummary() {
  const [startDate, setStartDate] = useState(isoDaysAgo(30));
  const [endDate, setEndDate] = useState(isoToday());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTip, setActiveTip] = useState(null);
  const [activeTripTip, setActiveTripTip] = useState(null);
  const [activeQuick, setActiveQuick] = useState(30);
  const [expandedVendor, setExpandedVendor] = useState(null);

  const load = (s, e) => {
    setLoading(true);
    setError(null);
    const startDt = new Date(s);
    const endDt = new Date(e);
    endDt.setHours(23, 59, 59, 999);
    fetchYieldSheetsByVendorHistory(toApiDate(s), toApiDate(e))
      .then(pos => {
        // 1 BU ≈ 80 lbs (industry standard for live blue crabs)


        // group by vendor — mirror client-side date filter from VendorHistory component
        const fmtD = d => `${String(d.getMonth()+1).padStart(2,'0')}/${String(d.getDate()).padStart(2,'0')}/${d.getFullYear()}`;
        const map = {};
        pos.forEach(p => {
          const name = p.crabberName ?? p.CrabberName ?? 'Unknown';
          const ty = Number(p.totalYield || 0);
          if (ty === 0) return;
          const dateStr = p.dateReceived ?? p.DateReceived;
          const date = dateStr ? new Date(dateStr) : null;
          if (date && (date < startDt || date > endDt)) return;
          const unit = (p.unitsOfMeasure ?? p.UnitsOfMeasure ?? 'BU').toUpperCase();
          const rawCrabs = Number(p.totalCrabs || 0);
          // Boxes come from the line items (BoxQty + BushelQty), which the crew keyed in
          // whole and which reconcile to the invoice - that is the real answer and it is
          // present on essentially every trip.
          // ⚠️ THE FALLBACK DIVISOR IS 45, NOT 80. A box is 45 lb for #1s and #2s and 50 lb
          // for #3s - measured across the 2026 season, grade 1 is 45 on 169 of 177 lines,
          // grade 2 on 91 of 111, grade 3 is 50 on 25 of 29 - and almost all pound volume is
          // #1s and #2s. Dividing by 80 understated a pound vendor by nearly half whenever
          // the line items were missing.
          const crabsBU = Number(p.totalCrabCount || 0) || (unit === 'LBS' ? rawCrabs / 45 : rawCrabs);
          if (!map[name]) map[name] = { name, trips: 0, totalCrabsBU: 0, totalCrabsRaw: 0, unit: 'BU', totalYield: 0, totalCost: 0, totalRetail: 0, mixedUnits: false, dates: [], tripDetails: [] };
          if (map[name].unit !== unit && map[name].trips > 0) map[name].mixedUnits = true;
          map[name].unit = unit;
          map[name].trips += 1;
          if (dateStr) map[name].dates.push(new Date(dateStr));
          map[name].totalCrabsBU += crabsBU;
          map[name].totalCrabsRaw += rawCrabs;
          map[name].totalYield += ty;
          map[name].totalCost += Number(p.totalAmount || 0) + Number(p.transportationCost || 0);
          map[name].totalRetail += Number(p.totalMaleRetailPrice || 0) + Number(p.totalFemaleRetailPrice || 0);
          const tripCost = Number(p.totalAmount || 0) + Number(p.transportationCost || 0);
          const tripRetail = Number(p.totalMaleRetailPrice || 0) + Number(p.totalFemaleRetailPrice || 0);
          map[name].tripDetails.push({
            date: dateStr ? fmtD(new Date(dateStr)) : '—',
            crabs: rawCrabs, crabsBU, unit,
            yield: ty,
            yieldPct: crabsBU > 0 ? (ty / crabsBU) * 100 : 0,
            cost: tripCost,
            retail: tripRetail,
            ratio: tripCost > 0 ? (tripRetail / tripCost - 1) * 100 : null,
          });
        });

        const vendors = Object.values(map).map(v => ({
          ...v,
          dates: v.dates.sort((a, b) => a - b).map(fmtD),
          yieldPct: v.totalCrabsBU > 0 ? (v.totalYield / v.totalCrabsBU) * 100 : null,
          ratio: v.totalCost > 0 ? (v.totalRetail / v.totalCost - 1) * 100 : null,
        }));

        // sort best ratio first
        vendors.sort((a, b) => (b.ratio ?? -Infinity) - (a.ratio ?? -Infinity));
        setRows(vendors);
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(startDate, endDate); }, []);

  const HEADERS = [
    { label: '#' },
    { label: 'Vendor' },
    { label: 'Trips' },
    { label: 'Total Crabs' },
    { label: 'Total Yield' },
    { label: 'Yield %', tip: 'red < 90%  ·  black 90–100%  ·  green > 100%' },
    { label: 'Total Cost' },
    { label: 'Total Retail' },
    { label: 'Ratio', tip: 'red < 80%  ·  black 80–110%  ·  green > 110%' },
  ];

  // grand totals — normalize all to BU for yield %
  const totals = rows.reduce((s, v) => {
    s.trips += v.trips;
    s.totalCrabsBU += v.totalCrabsBU;
    s.totalYield += v.totalYield;
    s.totalCost += v.totalCost;
    s.totalRetail += v.totalRetail;
    return s;
  }, { trips: 0, totalCrabsBU: 0, totalYield: 0, totalCost: 0, totalRetail: 0 });
  const totalsYieldPct = totals.totalCrabsBU > 0 ? (totals.totalYield / totals.totalCrabsBU) * 100 : null;
  const totalsRatio = totals.totalCost > 0 ? (totals.totalRetail / totals.totalCost - 1) * 100 : null;

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Vendor Summary</h2>
      </div>

      {/* Date filter */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-header">Date Range</div>
        <div style={{ padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>FROM</div>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="form-input" style={{ fontSize: 11 }} />
          </div>
          <div>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', marginBottom: 4 }}>TO</div>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="form-input" style={{ fontSize: 11 }} />
          </div>
          <button onClick={() => { setActiveQuick(null); load(startDate, endDate); }} className="btn btn-primary" style={{ fontSize: 11 }}>Search</button>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            {[30, 60, 90].map(d => (
              <button key={d} onClick={() => {
                const end = isoToday();
                const start = isoDaysAgo(d);
                setStartDate(start);
                setEndDate(end);
                setActiveQuick(d);
                load(start, end);
              }}
                style={{ fontSize: 10, padding: '2px 8px', borderRadius: 10, border: 'none', cursor: 'pointer', fontWeight: 700, background: activeQuick === d ? 'var(--navy)' : 'var(--gray-200)', color: activeQuick === d ? '#fff' : 'var(--gray-600)' }}>
                {d}d
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading && <div className="card" style={{ padding: 24, textAlign: 'center', color: 'var(--gray-500)', fontSize: 13 }}>Loading...</div>}
      {error && <div className="card" style={{ padding: 16, color: '#dc2626', fontSize: 12 }}>{error}</div>}

      {!loading && !error && rows.length === 0 && (
        <div className="card" style={{ padding: 24, textAlign: 'center', color: 'var(--gray-500)', fontSize: 13 }}>
          No vendor data found for this date range.
        </div>
      )}

      {!loading && !error && rows.length > 0 && (
        <div className="card">
          <div className="card-header">{rows.length} vendors · {totals.trips} trips</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#c8d8b8', borderBottom: '2px solid #b0c898' }}>
                  {HEADERS.map(({ label, tip }) => (
                    <th key={label}
                      style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: '#333', position: 'relative', whiteSpace: 'nowrap' }}
                      onMouseEnter={() => tip && setActiveTip(label)}
                      onMouseLeave={() => setActiveTip(null)}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                        {label}
                        {tip && <Info size={10} style={{ color: '#666', cursor: 'help', flexShrink: 0 }} />}
                      </span>
                      {tip && activeTip === label && (
                        <div style={{ position: 'absolute', top: '100%', right: 0, zIndex: 100, background: '#1e293b', color: '#fff', fontSize: 10, fontWeight: 400, padding: '5px 9px', borderRadius: 5, whiteSpace: 'nowrap', pointerEvents: 'none', marginTop: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.25)', textTransform: 'none', letterSpacing: 0 }}>
                          {tip}
                        </div>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((v, i) => {
                  const yieldColor = v.yieldPct === null ? 'inherit' : v.yieldPct < 90 ? '#dc2626' : v.yieldPct > 100 ? '#16a34a' : 'inherit';
                  const ratioColor = v.ratio === null ? 'inherit' : v.ratio < 80 ? '#dc2626' : v.ratio > 110 ? '#16a34a' : 'inherit';
                  const isExpanded = expandedVendor === v.name;
                  return (
                    <React.Fragment key={v.name}>
                    <tr style={{ borderBottom: '1px solid var(--gray-100)', background: isExpanded ? '#f0f7ff' : i % 2 === 1 ? '#f9fafb' : 'white', cursor: 'pointer' }}
                      onClick={() => setExpandedVendor(isExpanded ? null : v.name)}>
                      <td style={{ padding: '8px 12px', color: 'var(--gray-400)', fontSize: 11 }}>{isExpanded ? '▼' : '▶'}</td>
                      <td style={{ padding: '8px 12px', fontWeight: 700 }}>{v.name}</td>
                      <td style={{ padding: '8px 12px' }}
                        onMouseEnter={(e) => { const r = e.currentTarget.getBoundingClientRect(); setActiveTripTip({ name: v.name, x: r.left, y: r.bottom + 4 }); }}
                        onMouseLeave={() => setActiveTripTip(null)}>
                        <span style={{ cursor: 'help', borderBottom: '1px dotted var(--gray-400)' }}>{v.trips}</span>
                        {activeTripTip?.name === v.name && (
                          <div style={{ position: 'fixed', top: activeTripTip.y, left: activeTripTip.x, zIndex: 10000, background: '#1e293b', color: '#fff', fontSize: 10, fontWeight: 400, padding: '7px 10px', borderRadius: 5, pointerEvents: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.3)', minWidth: 110, maxHeight: 220, overflowY: 'auto' }}>
                            {v.dates.map((d, di) => <div key={di} style={{ lineHeight: '18px' }}>{d}</div>)}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '8px 12px' }}>
                        {v.totalCrabsRaw > 0
                          ? v.unit === 'LBS'
                            ? `${fmtNum(v.totalCrabsBU, 1)} BU (${fmtNum(v.totalCrabsRaw, 0)} lbs)`
                            : `${fmtNum(v.totalCrabsRaw, 0)} BU${v.mixedUnits ? '*' : ''}`
                          : '—'}
                      </td>
                      <td style={{ padding: '8px 12px' }}>{v.totalYield > 0 ? `${fmtNum(v.totalYield, 2)} BU` : '—'}</td>
                      <td style={{ padding: '8px 12px', fontWeight: 700, color: yieldColor }}>{v.yieldPct !== null ? fmtPct(v.yieldPct) : '—'}</td>
                      <td style={{ padding: '8px 12px' }}>{fmtMoney(v.totalCost)}</td>
                      <td style={{ padding: '8px 12px' }}>{fmtMoney(v.totalRetail)}</td>
                      <td style={{ padding: '8px 12px', fontWeight: 700, color: ratioColor }}>{v.ratio !== null ? fmtPct(v.ratio) : '—'}</td>
                    </tr>
                    {isExpanded && v.tripDetails.map((t, ti) => {
                      const tyColor = t.yieldPct < 90 ? '#dc2626' : t.yieldPct > 100 ? '#16a34a' : 'inherit';
                      const trColor = t.ratio === null ? 'inherit' : t.ratio < 80 ? '#dc2626' : t.ratio > 110 ? '#16a34a' : 'inherit';
                      return (
                        <tr key={ti} style={{ borderBottom: '1px solid var(--gray-50)', background: ti % 2 === 0 ? '#f8faff' : '#f0f5ff', fontSize: 11 }}>
                          <td style={{ padding: '6px 12px' }} />
                          <td style={{ padding: '6px 12px', paddingLeft: 24, color: 'var(--gray-600)' }}>{t.date}</td>
                          <td style={{ padding: '6px 12px' }} />
                          <td style={{ padding: '6px 12px', color: 'var(--gray-600)' }}>
                            {t.unit === 'LBS' ? `${fmtNum(t.crabsBU, 1)} BU (${fmtNum(t.crabs, 0)} lbs)` : `${fmtNum(t.crabs, 0)} BU`}
                          </td>
                          <td style={{ padding: '6px 12px', color: 'var(--gray-600)' }}>{fmtNum(t.yield, 2)} BU</td>
                          <td style={{ padding: '6px 12px', fontWeight: 600, color: tyColor }}>{fmtPct(t.yieldPct)}</td>
                          <td style={{ padding: '6px 12px', color: 'var(--gray-600)' }}>{fmtMoney(t.cost)}</td>
                          <td style={{ padding: '6px 12px', color: 'var(--gray-600)' }}>{fmtMoney(t.retail)}</td>
                          <td style={{ padding: '6px 12px', fontWeight: 600, color: trColor }}>{t.ratio !== null ? fmtPct(t.ratio) : '—'}</td>
                        </tr>
                      );
                    })}
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: '2px solid var(--gray-300)', background: '#f0f4f0', fontWeight: 700 }}>
                  <td style={{ padding: '8px 12px' }} />
                  <td style={{ padding: '8px 12px', fontSize: 11 }}>All Vendors</td>
                  <td style={{ padding: '8px 12px' }}>{totals.trips}</td>
                  <td style={{ padding: '8px 12px' }}>{totals.totalCrabsBU > 0 ? `${fmtNum(totals.totalCrabsBU, 0)} BU` : '—'}</td>
                  <td style={{ padding: '8px 12px' }}>{totals.totalYield > 0 ? `${fmtNum(totals.totalYield, 2)} BU` : '—'}</td>
                  <td style={{ padding: '8px 12px', color: totalsYieldPct === null ? 'inherit' : totalsYieldPct < 90 ? '#dc2626' : totalsYieldPct > 100 ? '#16a34a' : 'inherit' }}>
                    {totalsYieldPct !== null ? fmtPct(totalsYieldPct) : '—'}
                  </td>
                  <td style={{ padding: '8px 12px' }}>{fmtMoney(totals.totalCost)}</td>
                  <td style={{ padding: '8px 12px' }}>{fmtMoney(totals.totalRetail)}</td>
                  <td style={{ padding: '8px 12px', color: totalsRatio === null ? 'inherit' : totalsRatio < 80 ? '#dc2626' : totalsRatio > 110 ? '#16a34a' : 'inherit' }}>
                    {totalsRatio !== null ? fmtPct(totalsRatio) : '—'}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
